/**
 * Centralized Pricing Configuration
 * 
 * All tier pricing and limits should be defined here to avoid
 * hardcoded values scattered across components.
 * 
 * NOTE: In production, these values should ideally come from
 * the backend API (/api/pricing/tiers) to ensure consistency.
 */

export const TIER_PRICING = {
  trial: {
    name: 'Free',
    price: 0,
    priceDisplay: 'Free',
    vehicles: 5,
    cohosts: 0,
    teamMembers: 1,
  },
  solo: {
    name: 'Starter',
    price: 9.99,
    priceDisplay: '$9.99/month',
    vehicles: 15,
    cohosts: 0,
    teamMembers: 1,
  },
  team: {
    name: 'Pro',
    price: 29.99,
    priceDisplay: '$29.99/month',
    vehicles: 40,
    cohosts: 10,
    teamMembers: 3,
  },
  growth: {
    name: 'Business',
    price: 79.99,
    priceDisplay: '$79.99/month',
    vehicles: 75,
    cohosts: 25,
    teamMembers: 8,
  },
  enterprise: {
    name: 'Enterprise',
    price: 349,
    priceDisplay: '$349/month',
    vehicles: 'Unlimited',
    cohosts: 'Unlimited',
    teamMembers: 'Unlimited',
  },
  full_enterprise: {
    name: 'Full Enterprise Automation',
    price: 1200,
    priceDisplay: '$1,200/month',
    vehicles: 'Unlimited',
    cohosts: 'Unlimited',
    teamMembers: 'Unlimited',
  },
};

/**
 * Get tier pricing by tier name
 * @param {string} tier - The tier name (solo, team, growth, enterprise)
 * @returns {object} Tier pricing info, defaults to team if not found
 */
export const getTierPricing = (tier) => {
  return TIER_PRICING[tier?.toLowerCase()] || TIER_PRICING.team;
};

/**
 * Get tier price as number
 * @param {string} tier - The tier name
 * @returns {number} Price in dollars
 */
export const getTierPrice = (tier) => {
  return getTierPricing(tier).price;
};

/**
 * Get tier price as display string
 * @param {string} tier - The tier name
 * @returns {string} Price display string (e.g., "$99/month")
 */
export const getTierPriceDisplay = (tier) => {
  return getTierPricing(tier).priceDisplay;
};

/**
 * Get tier vehicle limit
 * @param {string} tier - The tier name
 * @returns {number|string} Vehicle limit or "Unlimited"
 */
export const getTierVehicleLimit = (tier) => {
  return getTierPricing(tier).vehicles;
};

export default TIER_PRICING;
